import React, { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Button } from "components/ui/button";
import { DisplayYears, Order, Sort, UserRole } from "lib/types";
import { Setting, VatInvoice } from "lib/models";
import { apiGet } from "components/Common/Axios";
import toast from "react-hot-toast";
import { RootState, useAppDispatch, useAppSelector } from "store";
import { Checkbox } from "components/ui/checkbox";
import { setSearchInvoiceStatus, setTaxYears } from "store/mainSlice";
import { cn, formatKoreanDate, getMonth, getYear } from "lib/utils";
import VatPaperPopup from "./VatPaperPopup";
import { Switch } from "components/ui/switch";
import { Label } from "components/ui/label";

export type VatInvoiceCheck = {
  vatInvoice: VatInvoice;
  checked: boolean;
};

// 세금계산서 발급
const VatApply = () => {
  const [sort, setSort] = useState<Sort>("region");
  const [order, setOrder] = useState<Order>("asc");
  const [searchString, setSearchString] = useState<string>("");
  const [selectedVatInvoices, setSelectedVatInvoices] = useState<VatInvoice[]>(
    []
  );
  const [isShowComplete, setIsShowComplete] = useState<boolean>(false);
  const [selectMonth, setSelectMonth] = useState<number>(0);

  const [isShowApply, setIsShowApply] = useState<boolean>(false);
  const [isShowCancel, setIsShowCancel] = useState<boolean>(false);

  const [isVatPaperOpen, setIsVatPaperOpen] = useState<boolean>(false);

  // 모든 Invoice선택 체크박스
  const [checkAllInvoice, setCheckAllInvoice] = useState<boolean>(false);

  // [VatInvoiceCheck List]
  // 모든 VatInvoiceCheck
  const [totalVatInvoiceCheck, setTotalVatInvoiceCheck] = useState<
    VatInvoiceCheck[]
  >([]);

  // 검색조건에 Filtering된 (화면에 보이는) VatInvoiceCheck
  const [filteredInvoiceCheck, setFilteredInvoiceCheck] = useState<
    VatInvoiceCheck[]
  >([]);

  // filteredInvoiceCheck에서 체크된것만 모아둔 것
  const [filteredInvoiceCheckSelected, setFilteredInvoiceCheckSelected] =
    useState<VatInvoiceCheck[]>([]);

  const [setting, setSetting] = useState<Setting | undefined>(undefined);

  const me = useAppSelector((state: RootState) => state.user.me);
  const taxYears = useAppSelector((state: RootState) => state.main.taxYears);
  const searchOption = useAppSelector(
    (state) => state.main.searchInvoiceOption
  );
  const dispatch = useAppDispatch();

  const loadVatInvoices = async () => {
    const response = await apiGet("/vat-invoice");
    if (response.error) {
      toast.error("세금계산서를 불러오는데 실패했습니다.");
      console.error("loadVatInvoices.error", response.error);
      return;
    }

    const vatInvoices = response.data as VatInvoice[];
    setTotalVatInvoiceCheck(
      vatInvoices.map((vatInvoice) => {
        return { vatInvoice: { ...vatInvoice }, checked: false };
      })
    );
  };

  const loadSetting = async () => {
    try {
      const response = await apiGet("/setting");
      if (response.error) {
        toast.error("시스템 설정을 불러오는데 실패했습니다.");
        console.error("loadSetting.error", response.error);
        return;
      }
      setSetting(response.data);
    } catch (error: any) {
      toast.error("시스템 설정을 불러오는데 실패했습니다.");
      console.error("loadSetting.error", error.message);
    }
  };

  useEffect(() => {
    if (searchOption) {
      setSearchString(searchOption.keyword);
      setSort(searchOption.sort as Sort);
      setOrder(searchOption.order as Order);
      setIsShowComplete(searchOption.isShowComplete);
      setSelectMonth(searchOption.selectMonth);
    }
    loadVatInvoices();
    loadSetting();
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // searchOption들이 바뀌면 다시 적용
  useEffect(() => {
    // store에 현재상태 저장
    dispatch(
      setSearchInvoiceStatus({
        keyword: searchString,
        sort,
        order,
        status: [],
        isShowComplete,
        selectMonth,
        isShowFree: false,
      })
    );

    search();
    //checkApplyCancelState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    totalVatInvoiceCheck,
    searchString,
    sort,
    order,
    isShowComplete,
    dispatch,
    taxYears,
    selectMonth,
  ]);

  // Invoice 전체 체크
  const handleCheckTotalInvoiceAll = async (checked: boolean) => {
    setCheckAllInvoice(checked);

    const filteredChecked = filteredInvoiceCheck.map((c: VatInvoiceCheck) => {
      return { ...c, checked };
    });
    setFilteredInvoiceCheck(filteredChecked);
    checkApplyCancelState(filteredChecked);
  };

  const checkApplyCancelState = (vatInvoiceCheck: VatInvoiceCheck[]) => {
    const selectedVatInvoices = vatInvoiceCheck
      .filter((c) => c.checked)
      .map((c) => c.vatInvoice);

    setSelectedVatInvoices(selectedVatInvoices);

    if (selectedVatInvoices.length === 0) {
      setIsShowApply(false);
      setIsShowCancel(false);
      return;
    }

    const applyCnt = selectedVatInvoices.filter(
      (c) => c.isIssuedCompleted === true
    ).length;
    const cancelCnt = selectedVatInvoices.filter(
      (c) => c.isIssuedCompleted === false
    ).length;

    if (applyCnt > 0 && cancelCnt > 0) {
      setIsShowApply(false);
      setIsShowCancel(false);
    } else if (applyCnt > 0) {
      setIsShowApply(false);
      setIsShowCancel(true);
    } else {
      setIsShowApply(true);
      setIsShowCancel(false);
    }
  };

  // 검색시 현재 검색어에 맞게 필터링
  const search = () => {
    const optionFilteredInvoice = totalVatInvoiceCheck.filter((s) => {
      return (
        (s.vatInvoice.vatCompany?.nickName.includes(searchString) ||
          s.vatInvoice.vatCompany?.name.includes(searchString) ||
          s.vatInvoice.vatCompany?.ceoName.includes(searchString) ||
          s.vatInvoice.vatCompany?.address.includes(searchString) ||
          s.vatInvoice.vatCompany?.depositName.includes(searchString) ||
          s.vatInvoice.schoolSales?.filter((s) =>
            s.school.name.includes(searchString)
          ).length > 0 ||
          s.vatInvoice.schoolSalesMaterial?.filter((s) =>
            s.school.name.includes(searchString)
          ).length > 0) &&
        (isShowComplete || !s.vatInvoice.isIssuedCompleted)
      );
    });

    if (sort === "class_date") {
      if (order === "asc") {
        optionFilteredInvoice.sort((a, b) => {
          return a.vatInvoice.issuedDate && b.vatInvoice.issuedDate
            ? a.vatInvoice.issuedDate > b.vatInvoice.issuedDate
              ? 1
              : -1
            : a.vatInvoice.issuedDate && !b.vatInvoice.issuedDate
            ? 1
            : !a.vatInvoice.issuedDate && b.vatInvoice.issuedDate
            ? -1
            : 1;
        });
      } else {
        optionFilteredInvoice.sort((a, b) => {
          return a.vatInvoice.issuedDate && b.vatInvoice.issuedDate
            ? a.vatInvoice.issuedDate > b.vatInvoice.issuedDate
              ? -1
              : 1
            : a.vatInvoice.issuedDate && !b.vatInvoice.issuedDate
            ? -1
            : !a.vatInvoice.issuedDate && b.vatInvoice.issuedDate
            ? 1
            : -1;
        });
      }
    } else if (sort === "name") {
      if (order === "asc") {
        optionFilteredInvoice.sort((a, b) => {
          return (a.vatInvoice.vatCompany?.name ?? "") >
            (b.vatInvoice.vatCompany?.name ?? "")
            ? 1
            : -1;
        });
      } else {
        optionFilteredInvoice.sort((a, b) => {
          return (a.vatInvoice.vatCompany?.name ?? "") <
            (b.vatInvoice.vatCompany?.name ?? "")
            ? 1
            : -1;
        });
      }
    } else if (sort === "region") {
      if (order === "asc") {
        optionFilteredInvoice.sort((a, b) => {
          return (a.vatInvoice.vatCompany?.address ?? "") >
            (b.vatInvoice.vatCompany?.address ?? "")
            ? 1
            : -1;
        });
      } else {
        optionFilteredInvoice.sort((a, b) => {
          return (a.vatInvoice.vatCompany?.address ?? "") <
            (b.vatInvoice.vatCompany?.address ?? "")
            ? 1
            : -1;
        });
      }
    }

    const filteredInvoice = optionFilteredInvoice.filter(
      (s) =>
        taxYears.includes(getYear(s.vatInvoice.issuedDate!)) &&
        (taxYears.length !== 1 ||
          selectMonth === 0 ||
          getMonth(s.vatInvoice.issuedDate!) === selectMonth)
    );

    setFilteredInvoiceCheck(filteredInvoice);
  };

  const popupVatPaper = (vatInvoice: VatInvoice) => {
    if (!vatInvoice) {
      console.error("vatInvoice = undefined");
      return;
    }
    setSelectedVatInvoices([vatInvoice]);
    setIsVatPaperOpen(true);
  };

  if (!setting) {
    return null;
  }

  const handleApply = () => {
    setSelectedVatInvoices(selectedVatInvoices);
    setIsVatPaperOpen(true);
  };

  const handleYearClick = (year: number) => {
    if (taxYears.includes(year)) {
      dispatch(setTaxYears(taxYears.filter((y) => y !== year)));
    } else {
      dispatch(setTaxYears([...taxYears, year]));
    }
  };

  return (
    <div className="text-sm pb-8">
      <VatPaperPopup
        open={isVatPaperOpen}
        onOpenChange={setIsVatPaperOpen}
        vatInvoices={selectedVatInvoices}
        totalVatInvoiceCheck={totalVatInvoiceCheck}
        setTotalVatInvoiceCheck={setTotalVatInvoiceCheck}
        setting={setting}
        isApplyMode={
          selectedVatInvoices.length === 1
            ? !selectedVatInvoices[0].isIssuedCompleted
            : isShowApply
        }
        isReadOnly={false}
      />
      <div className="flex flex-col xl:flex-row xl:justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50/70 px-4 py-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div>정렬</div>
          <Select value={sort} onValueChange={(v) => setSort(v as Sort)}>
            <SelectTrigger className="w-[120px] text-center">
              <SelectValue placeholder="1" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="class_date">공급일</SelectItem>
                <SelectItem value="name">거래처명</SelectItem>
                <SelectItem value="region">지역</SelectItem>
                <SelectItem value="stage">단계</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <Select value={order} onValueChange={(v) => setOrder(v as Order)}>
            <SelectTrigger className="w-[120px] text-center">
              <SelectValue placeholder="1" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="asc">오름차순</SelectItem>
                <SelectItem value="desc">내림차순</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>

          <div>검색</div>
          <input
            className="px-4 py-2 border border-gray-200 rounded-md w-[120px]"
            type="text"
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
          />

          <div className="xl:pl-10 flex items-center gap-2">
            <Switch
              checked={isShowComplete}
              onCheckedChange={(checked) => setIsShowComplete(checked)}
              id="isShowComplete"
            />
            <Label htmlFor="isShowComplete">발급요청완료 포함</Label>
          </div>
          <div className="flex flex-wrap items-center xl:px-2 gap-2">
            {DisplayYears.map((year) => (
              <div
                key={year}
                className={cn(
                  "text-xs rounded-md py-2 px-3 cursor-pointer w-[80px] text-center",
                  taxYears.includes(year)
                    ? "bg-indigo-600 text-white border border-indigo-600 shadow-sm"
                    : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                )}
                onClick={() => handleYearClick(year)}
              >
                {year}년
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {isShowApply && (
            <Button onClick={() => handleApply()} variant="primary">
              세금계산서 발급
            </Button>
          )}
          {isShowCancel && (
            <Button onClick={() => handleApply()} variant="primary">
              세금계산서 발급 취소
            </Button>
          )}
        </div>
      </div>
      {taxYears.length === 1 && (
        <div className="flex flex-wrap justify-center items-center gap-2 py-4">
          <div
            className={cn(
              "w-12 h-8 rounded-sm border-solid border border-gray-300 cursor-pointer flex items-center justify-center",
              selectMonth === 0
                ? "bg-indigo-600 text-white border border-indigo-600 shadow-sm"
                : "text-gray-800 hover:bg-gray-50"
            )}
            onClick={() => setSelectMonth(0)}
          >
            전체
          </div>
          {Array.from({ length: 12 }, (_, i) => (
            <div
              className={cn(
                "w-8 h-8 rounded-sm border-solid border border-gray-300 cursor-pointer flex items-center justify-center",
                selectMonth === i + 1
                  ? "bg-indigo-600 text-white border border-indigo-600 shadow-sm"
                  : "text-gray-800 hover:bg-gray-50"
              )}
              onClick={() => setSelectMonth(i + 1)}
            >
              <div>{i + 1}</div>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between items-center pb-2 px-2">
        <div>
          {/* {selectedVatInvoices.length > 0 && */}
          선택: {selectedVatInvoices.length}개{/* `}{" "} */}
        </div>
        <div>전체: {filteredInvoiceCheck.length}개 </div>
      </div>
      <div className="overflow-x-auto">
      <div className="min-w-[1300px]">
      <div className="grid grid-cols-[80px_80px_120px_180px_180px_80px_100px_100px_90px_140px_1fr] border-solid border-b border-t border-gray-200 py-2 bg-gray-50 text-gray-500 font-medium pl-4">
        <div className="flex items-center gap-x-4">
          <Checkbox
            id="checkAll"
            className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
            checked={checkAllInvoice}
            onCheckedChange={handleCheckTotalInvoiceAll}
          />
          <div>발급</div>
        </div>
        <div className="text-center">VID</div>
        <div className="">학교명</div>
        <div className="">거래처명</div>
        <div className="">발급처명</div>
        <div className="">담당자</div>
        <div className="text-right">총액</div>
        <div className="text-right">공급가액</div>
        <div className="text-right">세액</div>
        <div className="text-center">발급일</div>
        <div className="">내용</div>
      </div>
      <div>
        {filteredInvoiceCheck.map((s: VatInvoiceCheck, index: number) => (
          <div
            key={s.vatInvoice.id}
            onClick={(e) => popupVatPaper(s.vatInvoice)}
            className={cn(
              "grid grid-cols-[80px_80px_120px_180px_180px_80px_100px_100px_90px_140px_1fr] border-solid border-b border-gray-200 pl-4 py-2 items-center cursor-pointer",
              s.checked
                ? "bg-indigo-50 hover:bg-indigo-100"
                : "bg-white hover:bg-gray-50"
            )}
          >
            <div className="flex items-center gap-x-2">
              <Checkbox
                className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                checked={s.checked}
                onCheckedChange={(checked: boolean) => {
                  const filteredInvoiceChecks = filteredInvoiceCheck.map(
                    (invoiceCheck) =>
                      invoiceCheck.vatInvoice.id === s.vatInvoice.id
                        ? { ...invoiceCheck, checked }
                        : invoiceCheck
                  );
                  setFilteredInvoiceCheck(filteredInvoiceChecks);
                  checkApplyCancelState(filteredInvoiceChecks);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
              {s.vatInvoice.isIssuedCompleted && (
                <div className="bg-cyan-500 rounded-md w-12 h-[25px] text-center text-white text-xs pt-[4px]">
                  발급
                </div>
              )}
            </div>
            <div className="text-center">{s.vatInvoice.id}</div>
            <div className="whitespace-pre-wrap">
              {Array.from(
                new Set(
                  s.vatInvoice.schoolSalesMaterial
                    .map((sales) => sales.school?.name ?? "")
                    .concat(
                      s.vatInvoice.schoolSales.map(
                        (sales) => sales.school?.name ?? ""
                      )
                    )
                )
              ).join("\n")}
            </div>
            <div>{s.vatInvoice.vatCompany?.nickName}</div>

            <div>{s.vatInvoice.vatCompany?.name}</div>
            <div>
              {s.vatInvoice.schoolSales.length > 0
                ? s.vatInvoice.schoolSales[0].user?.name
                : s.vatInvoice.schoolSalesMaterial.length > 0
                ? s.vatInvoice.schoolSalesMaterial[0].user?.name
                : ""}
            </div>
            <div className="text-right">
              {s.vatInvoice.totalAmt.toLocaleString()}
            </div>
            <div className="text-right">
              {s.vatInvoice.supplyAmt.toLocaleString()}
            </div>
            <div className="text-right">
              {s.vatInvoice.taxAmt.toLocaleString()}
            </div>
            <div className="text-center">
              {formatKoreanDate(s.vatInvoice.issuedDate!)}
            </div>
            <div className="whitespace-pre-wrap">
              {[
                s.vatInvoice.supplyContent1,
                s.vatInvoice.supplyContent2,
                s.vatInvoice.supplyContent3,
                s.vatInvoice.supplyContent4,
              ]
                .filter((content) => content !== "")
                .join(", ")}
            </div>
          </div>
        ))}
        <div className="grid grid-cols-[80px_80px_120px_180px_180px_80px_100px_100px_90px_140px_1fr] border-solid border-b bg-gray-50 border-gray-200 pl-4 py-3 text-gray-800 font-semibold">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div>총계</div>
          <div>{filteredInvoiceCheck.length} 개</div>
          <div className="text-right">
            {filteredInvoiceCheck
              .reduce((acc, cur) => acc + cur.vatInvoice.totalAmt, 0)
              .toLocaleString()}
          </div>
          <div className="text-right">
            {filteredInvoiceCheck
              .reduce((acc, cur) => acc + cur.vatInvoice.supplyAmt, 0)
              .toLocaleString()}
          </div>
          <div className="text-right">
            {filteredInvoiceCheck
              .reduce((acc, cur) => acc + cur.vatInvoice.taxAmt, 0)
              .toLocaleString()}
          </div>
          <div></div>
          <div></div>
        </div>
        <div className="grid grid-cols-[80px_80px_120px_180px_180px_80px_100px_100px_90px_140px_1fr] border-solid border-b bg-gray-50 border-gray-200 pl-4 py-3 text-gray-800 font-semibold">
          <div></div>
          <div></div> <div></div>
          <div></div>
          <div>선택계</div>
          <div>
            {
              filteredInvoiceCheck.filter(
                (invoiceCheck) => invoiceCheck.checked
              ).length
            }{" "}
            개
          </div>
          <div className="text-right">
            {filteredInvoiceCheck
              .filter((invoiceCheck) => invoiceCheck.checked)
              .reduce((acc, cur) => acc + cur.vatInvoice.totalAmt, 0)
              .toLocaleString()}
          </div>
          <div className="text-right">
            {filteredInvoiceCheck
              .filter((invoiceCheck) => invoiceCheck.checked)
              .reduce((acc, cur) => acc + cur.vatInvoice.supplyAmt, 0)
              .toLocaleString()}
          </div>
          <div className="text-right">
            {filteredInvoiceCheck
              .filter((invoiceCheck) => invoiceCheck.checked)
              .reduce((acc, cur) => acc + cur.vatInvoice.taxAmt, 0)
              .toLocaleString()}
          </div>
          <div></div>
          <div></div>
        </div>
      </div>
      </div>
      </div>
    </div>
  );
};

export default VatApply;
